import * as React from 'react';
import * as _ from 'lodash';
import * as Yup from 'yup';
import {Formik, Form, Field, FieldProps, FormikHelpers} from 'formik';
import styled from 'styled-components';
import {transparentize} from 'polished';
import Input, {InputProps} from 'components/Input';
import {IconEnter} from 'icons';
import {transition} from 'helpers/css';
import outline from 'helpers/outline';

export interface InputSubmitProps extends Omit<InputProps, 'onSubmit'> {
  onSubmit?(values: InputSubmitValues): void;
}

export const Container = styled.div` 
  pointer-events: auto;
  position: relative;
`;

export const InputWrapper = styled.div`
  #input-submit-field {
    padding-right: 60px;
    width: 100%;
  }
`;

export const Submit = styled.button`
  margin: auto;
  position: absolute;
  top: 0;
  right: 5px;
  bottom: 0;
  
  width: 35px;
  height: 35px;
  border-radius: 5px;
  ${transition()};
  
  color: ${({theme}) => theme.variables.color};
  background: ${({theme}) => `
    ${transparentize(.95, theme.variables.color)};
  `};
  
  &:hover {
    background: ${({theme}) => `
      ${transparentize(.9, theme.variables.color)};
    `};
  }
  
  &:active {
    background: ${({theme}) => `
      ${transparentize(.85, theme.variables.color)};
    `};
  }
  
  &:focus-visible {
    ${outline(0, 1)};
  }
  
  svg {
    fill: currentColor;
  }
`;

export interface InputSubmitValues {
  value: InputSubmitProps['value'];
}

/**
 * The input field with submit button
 * @param {InputSubmitProps} props
 * @return {React.ReactElement<InputSubmitProps>}
 * @constructor
 */
export default function InputSubmit(
    props: InputSubmitProps,
): React.ReactElement<InputSubmitProps> {
  const initialValues: InputSubmitValues = {
    value: '',
  };

  const inputProps = (
    _.omit(props, 'onSubmit')
  );

  const Schema = Yup.object().shape({
    value: Yup.string().required(),
  });

  /**
   * The handle of submit
   * @param {InputSubmitValues} values
   * @param {FormikHelpers<InputSubmitValues>} formikHelpers
   */
  function handleSubmit(
      values: InputSubmitValues,
      formikHelpers: FormikHelpers<InputSubmitValues>,
  ): void {
    props.onSubmit && props.onSubmit(values);
    formikHelpers.resetForm();
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Schema}
      onSubmit={handleSubmit}
    >
      {() => (
        <Form>
          <Container>
            <Field name="value">
              {({field}: FieldProps) => (
                <InputWrapper>
                  <Input
                    id="input-submit-field"
                    {...inputProps}
                    {...field}
                  />
                </InputWrapper>
              )}
            </Field>
            <Submit type="submit">
              <IconEnter />
            </Submit>
          </Container>
        </Form>
      )}
    </Formik>
  );
}
